import { useCallback } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { useDiscoveryAiCreditsQuery } from './__generated__/useDiscoveryAiCreditsQuery.graphql';
import { useDiscoveryAiCreditsBuyMutation } from './__generated__/useDiscoveryAiCreditsBuyMutation.graphql';

/**
 * The credit pools discovery can gate on:
 *  - `GENERATIVE_AI` — the single shared pool behind all four AI Creative Tools (image generation, design
 *    generation, product staging and virtual try-on), so one query governs all of them;
 *  - `VECTORIZE` — the AI Vectorizer's own pool. The Vectorizer is a discovery PRINT METHOD rather than an AI
 *    tool, but its credits behave identically (same `serviceCredit` fields, same trial grant), so it reuses
 *    this controller instead of duplicating it.
 */
export type DiscoveryCreditPool = 'GENERATIVE_AI' | 'VECTORIZE';

/**
 * The seller's discovery credit situation for one pool. The panel uses it to decide, at the moment the
 * merchant engages the feature, whether to run it for real or offer a credit action first:
 *  - `'needs-activation'` (CASE 1): the seller has never activated the free trial nor bought credits → show
 *    the "billed via credits" activation banner, which grants the trial on confirm.
 *  - `'has-credits'` (CASE 2): trial or paid credits are available (or the plan is unlimited) → run the real
 *    tool behind the usual "using your credits" warning.
 *  - `'out-of-credits'` (CASE 3): the trial/purchase happened before but the balance is now spent → show the
 *    buy-credits dialog. (Discovery never auto-refills, so the seller must explicitly buy the next pack.)
 */
export type DiscoveryAiCreditCase = 'needs-activation' | 'has-credits' | 'out-of-credits';

export interface DiscoveryAiCreditState {
    creditCase: DiscoveryAiCreditCase;
    /** The resolved balance for the pool (0 when there is no row yet). */
    credit: number;
    /** True on unlimited plans — always treated as has-credits regardless of the number. */
    unlimited: boolean;
}

export interface UseDiscoveryAiCreditsReturn {
    /**
     * Queries the pool's balance (network-only) and classifies it into one of the three credit cases WITHOUT
     * any side effect — in particular it no longer auto-grants the trial (that is now an explicit user
     * action, from the activation banner). Call it on discovery entry, on each engagement, and after a credit
     * mutation to re-resolve. Never throws — a failed query resolves to 'out-of-credits'.
     */
    resolve: () => Promise<DiscoveryAiCreditState>;
    /**
     * Grants the one-time free trial pack (credits: 0 + isForTrial → the backend awards the fixed trial
     * amount) with autoRefill DISABLED, so exhausting it never silently charges a paid pack from discovery.
     * Resolves true on success. Called from the CASE 1 activation dialog.
     */
    grantTrial: () => Promise<boolean>;
}

/**
 * Discovery-mode credit controller (backoffice side). The customizer has no GraphQL client, so the host
 * owns the Relay decisions — query the balance and grant/buy on explicit user action. Unlike v1 this resolves
 * the credit CASE for the panel to gate on; it no longer silently grants the trial or streams a real/mockup
 * mode (mockup mode was removed).
 *
 * `pool` selects which balance to govern (see {@link DiscoveryCreditPool}); it defaults to the shared AI
 * Creative Tools pool, so the original call site is unchanged.
 */
export function useDiscoveryAiCredits(pool: DiscoveryCreditPool = 'GENERATIVE_AI'): UseDiscoveryAiCreditsReturn {
    const relayEnvironment = useRelayEnvironment();

    const [grantTrialCredits] = useMutation<useDiscoveryAiCreditsBuyMutation>(graphql`
        mutation useDiscoveryAiCreditsBuyMutation($input: ServiceRechargeCreditsInput!) {
            buyServiceCredits(input: $input) {
                result
                errorMessage
            }
        }
    `);

    // Reads the authoritative balance. network-only so a re-resolve reflects freshly consumed/granted credits
    // rather than a stale cache. Returns null on failure so the caller degrades to 'out-of-credits'.
    const fetchCredit = useCallback(async () => {
        try {
            const data = await fetchQuery<useDiscoveryAiCreditsQuery>(
                relayEnvironment,
                graphql`
                    query useDiscoveryAiCreditsQuery($serviceName: ServicesWithCredits!) {
                        serviceCredit(serviceName: $serviceName) {
                            credit
                            unlimitedCredits
                            trialCreditsUsed
                            hasSellerPurchasedCredits
                        }
                    }
                `,
                { serviceName: pool },
                { fetchPolicy: 'network-only' }
            ).toPromise();
            return data?.serviceCredit ?? null;
        } catch (error) {
            console.error(`Discovery credits: failed to query serviceCredit for ${pool}:`, error);
            return null;
        }
    }, [relayEnvironment, pool]);

    const resolve = useCallback(async (): Promise<DiscoveryAiCreditState> => {
        const serviceCredit = await fetchCredit();

        const unlimited = serviceCredit?.unlimitedCredits === true;
        const credit = serviceCredit?.credit ?? 0;
        const hasCredits = unlimited || credit > 0;

        // Eligible for the one-time free trial when there is no credit row yet, or the seller has neither been
        // granted the trial nor bought credits. (trialCreditsUsed means "trial already granted", NOT
        // "trial exhausted" — a fresh trial pack reads trialCreditsUsed: true.)
        const eligibleForTrial =
            !serviceCredit || (!serviceCredit.trialCreditsUsed && !serviceCredit.hasSellerPurchasedCredits);

        // hasCredits wins first: available credits are usable whatever the activation history.
        const creditCase: DiscoveryAiCreditCase = hasCredits
            ? 'has-credits'
            : eligibleForTrial
              ? 'needs-activation'
              : 'out-of-credits';

        return { creditCase, credit, unlimited };
    }, [fetchCredit]);

    const grantTrial = useCallback((): Promise<boolean> => {
        return new Promise<boolean>((resolveGrant) => {
            grantTrialCredits({
                variables: {
                    input: {
                        service: pool,
                        shopifyChargeID: null,
                        credits: 0,
                        // Discovery never auto-refills — exhausting the trial must not charge a paid pack. The
                        // merchant is prompted to buy the next pack explicitly (CASE 3).
                        autoRefill: false,
                        currencyCode: 'USD',
                        isForTrial: true,
                        returnUrlFromApproval: null,
                    },
                },
                onCompleted: (response) => {
                    const result = response.buyServiceCredits?.result;
                    resolveGrant(result === 'SUCCESS' || result === 'SUCCESS_FOR_TRIAL');
                },
                onError: (error) => {
                    console.warn(`Discovery credits: ${pool} trial grant failed:`, error);
                    resolveGrant(false);
                },
            });
        });
    }, [grantTrialCredits, pool]);

    return { resolve, grantTrial };
}

export default useDiscoveryAiCredits;
